import React, { useEffect, useRef, useState } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { CheckCircle, Phone, Shield, Clock, Award, Loader2, AlertCircle } from 'lucide-react';
import { APP_CONFIG } from '../../config/app.config';
import {
  EMPTY_QUOTE_FORM, QUOTE_SERVICE_OPTIONS, readQuotePrefill,
  type QuoteForm, type QuotePrefill,
} from '../../utils/quotePrefill';
import { enquiryErrorMessage, sendEnquiryEmail } from '../../utils/sendEnquiryEmail';

type Step = 1 | 2 | 3 | 4;

const GetQuotePage: React.FC = () => {
  const location = useLocation();
  const [step, setStep] = useState<Step>(1);
  const [submitted, setSubmitted] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [submitError, setSubmitError] = useState<string | null>(null);
  const [form, setForm] = useState<QuoteForm>(() => ({ ...EMPTY_QUOTE_FORM, ...readQuotePrefill(location.state) }));

  const update = (k: string, v: string) => setForm(f => ({ ...f, [k]: v }));

  const startOver = (prefill: QuotePrefill | null = null) => {
    setForm({ ...EMPTY_QUOTE_FORM, ...prefill });
    setStep(1);
    setSubmitted(false);
    setSubmitError(null);
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  const isFirstRender = useRef(true);
  useEffect(() => {
    if (isFirstRender.current) { isFirstRender.current = false; return; }
    const prefill = readQuotePrefill(location.state);
    if (prefill) startOver(prefill);
    else if (submitted) startOver();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [location.key]);

  const stepLabels = ['Contact Info', 'Property Details', 'Service Info', 'Review & Submit'];

  const handleSubmit = async () => {
    if (!form.name.trim()) {
      setSubmitError('Please enter your full name.');
      setStep(1);
      return;
    }
    if (!form.phone.trim() && !form.email.trim()) {
      setSubmitError('Please provide either a phone number or an email address.');
      setStep(1);
      return;
    }
    if (!form.address.trim()) {
      setSubmitError('Please enter the property address.');
      setStep(1);
      return;
    }

    setSubmitError(null);
    setSubmitting(true);

    try {
      await sendEnquiryEmail({
        fullName: form.name,
        phoneNumber: form.phone,
        emailAddress: form.email,
        propertyAddress: form.address.trim(),
        city: form.city.trim() || undefined,
        roofAge: form.roofAge || undefined,
        roofType: form.roofType || undefined,
        squareFootage: form.squareFootage || undefined,
        serviceNeeded: form.service || undefined,
        urgency: form.urgency || undefined,
        additionalNotes: form.notes.trim() || undefined,
      });

      setSubmitted(true);
    } catch (err) {
      setSubmitError(enquiryErrorMessage(
        err,
        'Something went wrong submitting your request. Please try again or call us directly.'
      ));
    } finally {
      setSubmitting(false);
    }
  };

  if (submitted) {
    return (
      <div style={{ minHeight: '100vh', display: 'flex', alignItems: 'center', justifyContent: 'center', background: 'var(--bg-secondary)', padding: '2rem', paddingTop: '6rem' }}>
        <div style={{ textAlign: 'center', maxWidth: '520px' }}>
          <div style={{ width: '5rem', height: '5rem', borderRadius: '50%', background: 'linear-gradient(135deg, #1493e6, #0072c6)', display: 'flex', alignItems: 'center', justifyContent: 'center', margin: '0 auto 1.5rem' }}>
            <CheckCircle size={40} color="white" />
          </div>
          <h1 style={{ fontWeight: 800, fontSize: '2rem', color: 'var(--text-primary)', marginBottom: '0.75rem' }}>Quote Request Received!</h1>
          <p style={{ color: 'var(--text-secondary)', lineHeight: 1.7, marginBottom: '2rem' }}>
            Thanks, <strong>{form.name}</strong>! We've received your request and will contact you within 2 hours to schedule your free inspection.
          </p>
          <div className="card" style={{ marginBottom: '2rem', textAlign: 'left' }}>
            <p style={{ fontWeight: 600, marginBottom: '0.5rem', color: 'var(--text-primary)' }}>What happens next:</p>
            {['Our team reviews your request', 'We call to confirm your appointment', 'Free on-site inspection (no charge)', 'Receive detailed written estimate'].map((s, i) => (
              <div key={i} style={{ display: 'flex', gap: '0.75rem', alignItems: 'center', marginBottom: '0.5rem' }}>
                <div style={{ width: '1.5rem', height: '1.5rem', borderRadius: '50%', background: '#1493e6', color: 'white', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: '0.75rem', fontWeight: 700, flexShrink: 0 }}>{i + 1}</div>
                <span style={{ color: 'var(--text-secondary)', fontSize: '0.875rem' }}>{s}</span>
              </div>
            ))}
          </div>
          <div style={{ display: 'flex', gap: '1rem', justifyContent: 'center', flexWrap: 'wrap' }}>
            <a href={`tel:${APP_CONFIG.phoneRaw}`} className="btn-primary"><Phone size={16} /> Call Us Now</a>
            <button onClick={() => startOver()} className="btn-secondary">Start Another Request</button>
            <Link to="/" className="btn-secondary">Return Home</Link>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div style={{ minHeight: '100vh', background: 'var(--bg-secondary)', paddingTop: '5rem' }}>
      {/* Header */}
      <div style={{ background: 'linear-gradient(135deg, #050608, #0072c6)', padding: '3rem 1.5rem', textAlign: 'center' }}>
        <h1 style={{ color: 'white', fontWeight: 800, fontSize: '2.25rem', marginBottom: '0.5rem' }}>Get Your Free Estimate</h1>
        <p style={{ color: '#e6edf5', maxWidth: '480px', margin: '0 auto' }}>
          No obligation. Typically respond within 2 hours. One of our licensed estimators will inspect your property at no charge.
        </p>
      </div>

      <div className="container-xl" style={{ padding: '3rem 1.5rem', maxWidth: '760px' }}>
        {/* Progress */}
        <div style={{ display: 'flex', alignItems: 'center', marginBottom: '2.5rem' }}>
          {stepLabels.map((label, i) => {
            const n = (i + 1) as Step;
            const done = step > n;
            const active = step === n;
            return (
              <React.Fragment key={label}>
                <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', flex: 1 }}>
                  <div style={{
                    width: '2.25rem', height: '2.25rem', borderRadius: '50%', display: 'flex', alignItems: 'center', justifyContent: 'center',
                    fontWeight: 700, fontSize: '0.85rem',
                    background: done ? '#1493e6' : active ? '#1493e6' : 'var(--bg-secondary)',
                    color: (done || active) ? 'white' : 'var(--text-muted)',
                    border: `2px solid ${done ? '#1493e6' : active ? '#1493e6' : 'var(--border-color)'}`,
                    transition: 'all 0.3s',
                  }}>
                    {done ? <CheckCircle size={14} /> : n}
                  </div>
                  <span style={{ fontSize: '0.7rem', marginTop: '0.375rem', color: active ? '#1493e6' : 'var(--text-muted)', fontWeight: active ? 600 : 400, whiteSpace: 'nowrap' }}>
                    {label}
                  </span>
                </div>
                {i < 3 && <div style={{ flex: 1, height: '2px', background: step > n ? '#1493e6' : 'var(--border-color)', transition: 'background 0.3s', margin: '0 0.25rem', marginTop: '-1rem' }} />}
              </React.Fragment>
            );
          })}
        </div>

        <div className="card">
          {/* Step 1 */}
          {step === 1 && (
            <div>
              <h2 style={{ fontWeight: 700, fontSize: '1.25rem', marginBottom: '1.5rem', color: 'var(--text-primary)' }}>Contact Information</h2>
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '1.25rem' }}>
                <div>
                  <label className="label">Full Name *</label>
                  <input className="input-field" value={form.name} onChange={e => update('name', e.target.value)} placeholder="John Smith" />
                </div>
                <div>
                  <label className="label">Phone Number</label>
                  <input className="input-field" value={form.phone} onChange={e => update('phone', e.target.value)} placeholder="(904) 555-0000" />
                </div>
                <div style={{ gridColumn: '1 / -1' }}>
                  <label className="label">Email Address</label>
                  <input className="input-field" type="email" value={form.email} onChange={e => update('email', e.target.value)} placeholder="john@email.com" />
                </div>
                <div style={{ gridColumn: '1 / -1', marginTop: '-0.4rem' }}>
                  <p style={{ color: 'var(--text-secondary)', fontSize: '0.82rem', margin: 0 }}>
                    A <strong style={{ color: 'var(--text-primary)' }}>phone number or an email address</strong> is enough —
                    give us whichever you would rather be contacted on.
                  </p>
                </div>
                <div style={{ gridColumn: '1 / -1' }}>
                  <label className="label">Property Address *</label>
                  <input className="input-field" value={form.address} onChange={e => update('address', e.target.value)} placeholder="123 Main St" />
                </div>
                <div>
                  <label className="label">City</label>
                  <input className="input-field" value={form.city} onChange={e => update('city', e.target.value)} placeholder="Jacksonville" />
                </div>
              </div>
            </div>
          )}

          {/* Step 2 */}
          {step === 2 && (
            <div>
              <h2 style={{ fontWeight: 700, fontSize: '1.25rem', marginBottom: '1.5rem', color: 'var(--text-primary)' }}>Property Details</h2>
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '1.25rem' }}>
                <div>
                  <label className="label">Roof Age (approx.)</label>
                  <select className="input-field" value={form.roofAge} onChange={e => update('roofAge', e.target.value)}>
                    <option value="">Select...</option>
                    <option>Under 5 years</option>
                    <option>5–10 years</option>
                    <option>10–15 years</option>
                    <option>15–20 years</option>
                    <option>20+ years</option>
                    <option>Unknown</option>
                  </select>
                </div>
                <div>
                  <label className="label">Roof Type</label>
                  <select className="input-field" value={form.roofType} onChange={e => update('roofType', e.target.value)}>
                    <option value="">Select...</option>
                    <option>Asphalt Shingles</option>
                    <option>Metal Roofing</option>
                    <option>Tile / Slate</option>
                    <option>Flat / TPO / EPDM</option>
                    <option>Cedar Shake</option>
                    <option>Unknown</option>
                  </select>
                </div>
                <div>
                  <label className="label">Approximate Sq Footage</label>
                  <select className="input-field" value={form.squareFootage} onChange={e => update('squareFootage', e.target.value)}>
                    <option value="">Select...</option>
                    <option>Under 1,000 sq ft</option>
                    <option>1,000–1,500 sq ft</option>
                    <option>1,500–2,000 sq ft</option>
                    <option>2,000–2,500 sq ft</option>
                    <option>2,500–3,500 sq ft</option>
                    <option>3,500+ sq ft</option>
                    <option>Commercial</option>
                  </select>
                </div>
              </div>
            </div>
          )}

          {/* Step 3 */}
          {step === 3 && (
            <div>
              <h2 style={{ fontWeight: 700, fontSize: '1.25rem', marginBottom: '1.5rem', color: 'var(--text-primary)' }}>Service Information</h2>
              <div style={{ display: 'flex', flexDirection: 'column', gap: '1.25rem' }}>
                <div>
                  <label className="label">Service Needed *</label>
                  <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '0.75rem', marginTop: '0.25rem' }}>
                    {QUOTE_SERVICE_OPTIONS.map(s => (
                      <button key={s} onClick={() => update('service', s)} style={{
                        padding: '0.75rem', borderRadius: '0.625rem', border: `2px solid ${form.service === s ? '#1493e6' : 'var(--border-color)'}`,
                        background: form.service === s ? 'rgb(20 147 230 / 0.08)' : 'var(--bg-secondary)',
                        color: form.service === s ? '#1493e6' : 'var(--text-secondary)',
                        cursor: 'pointer', fontWeight: form.service === s ? 600 : 400, fontSize: '0.85rem', transition: 'all 0.2s',
                      }}>
                        {s}
                      </button>
                    ))}
                  </div>
                </div>
                <div>
                  <label className="label">Urgency</label>
                  <select className="input-field" value={form.urgency} onChange={e => update('urgency', e.target.value)}>
                    <option value="">Select...</option>
                    <option>Emergency — ASAP</option>
                    <option>Within 1 week</option>
                    <option>Within 2–4 weeks</option>
                    <option>1–3 months</option>
                    <option>Just getting quotes</option>
                  </select>
                </div>
                <div>
                  <label className="label">Additional Notes</label>
                  <textarea className="input-field" rows={4} placeholder="Describe any visible damage, specific concerns, or questions..." value={form.notes} onChange={e => update('notes', e.target.value)} style={{ resize: 'vertical' }} />
                </div>
              </div>
            </div>
          )}

          {/* Step 4 */}
          {step === 4 && (
            <div>
              <h2 style={{ fontWeight: 700, fontSize: '1.25rem', marginBottom: '1.5rem', color: 'var(--text-primary)' }}>Review & Submit</h2>

              {submitError && (
                <div style={{
                  display: 'flex', alignItems: 'flex-start', gap: '0.5rem',
                  background: 'rgb(220 38 38 / 0.08)', border: '1px solid rgb(220 38 38 / 0.25)',
                  borderRadius: '0.5rem', padding: '0.75rem 1rem', marginBottom: '1.5rem',
                }}>
                  <AlertCircle size={18} color="#dc2626" style={{ flexShrink: 0, marginTop: '0.1rem' }} />
                  <span style={{ fontSize: '0.85rem', color: '#dc2626' }}>{submitError}</span>
                </div>
              )}

              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '1rem', marginBottom: '1.5rem' }}>
                {[
                  { label: 'Name', value: form.name },
                  { label: 'Phone', value: form.phone },
                  { label: 'Email', value: form.email || '—' },
                  { label: 'Address', value: form.address },
                  { label: 'Service', value: form.service || '—' },
                  { label: 'Urgency', value: form.urgency || '—' },
                  { label: 'Roof Age', value: form.roofAge || '—' },
                  { label: 'Roof Type', value: form.roofType || '—' },
                ].map(item => (
                  <div key={item.label}>
                    <p style={{ fontSize: '0.75rem', color: 'var(--text-muted)', marginBottom: '0.25rem' }}>{item.label}</p>
                    <p style={{ fontWeight: 600, color: 'var(--text-primary)', fontSize: '0.9rem', margin: 0 }}>{item.value}</p>
                  </div>
                ))}
              </div>
              {form.notes && (
                <div style={{ marginBottom: '1.5rem' }}>
                  <p style={{ fontSize: '0.75rem', color: 'var(--text-muted)', marginBottom: '0.25rem' }}>Notes</p>
                  <p style={{ color: 'var(--text-secondary)', fontSize: '0.875rem', fontStyle: 'italic' }}>{form.notes}</p>
                </div>
              )}
              <div className="card" style={{ background: 'rgb(20 147 230 / 0.05)', border: '1px solid rgb(20 147 230 / 0.2)', marginBottom: '0.5rem' }}>
                <div style={{ display: 'flex', gap: '0.75rem', flexWrap: 'wrap' }}>
                  {[
                    { icon: <Shield size={16} color="#1493e6" />, text: '100% Free — No Obligation' },
                    { icon: <Clock size={16} color="#1493e6" />, text: 'Response within 2 hours' },
                    { icon: <Award size={16} color="#1493e6" />, text: 'Licensed & Insured' },
                  ].map(t => (
                    <div key={t.text} style={{ display: 'flex', alignItems: 'center', gap: '0.375rem' }}>
                      {t.icon}<span style={{ fontSize: '0.8rem', color: 'var(--text-secondary)' }}>{t.text}</span>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          )}

          {/* Navigation */}
          <div style={{ display: 'flex', justifyContent: 'space-between', marginTop: '2rem', paddingTop: '1.5rem', borderTop: '1px solid var(--border-color)' }}>
            {step > 1 ? (
              <button onClick={() => setStep((step - 1) as Step)} className="btn-secondary" disabled={submitting}>← Previous</button>
            ) : <div />}
            {step < 4 ? (
              <button onClick={() => setStep((step + 1) as Step)} className="btn-primary">Continue →</button>
            ) : (
              <button onClick={handleSubmit} className="btn-primary" style={{ minWidth: '180px' }} disabled={submitting}>
                {submitting ? (
                  <>
                    <Loader2 size={16} className="spin" /> Submitting...
                  </>
                ) : (
                  <>
                    <CheckCircle size={16} /> Submit Request
                  </>
                )}
              </button>
            )}
          </div>
        </div>

        {/* Trust Row */}
        <div style={{ display: 'flex', flexWrap: 'wrap', gap: '1.5rem', justifyContent: 'center', marginTop: '2rem' }}>
          {['Owens Corning Preferred Contractor', 'Locally & Family Owned', '10-Year Warranty', '5.0 Google Rating', 'Free Estimates'].map(t => (
            <div key={t} style={{ display: 'flex', alignItems: 'center', gap: '0.375rem' }}>
              <CheckCircle size={14} color="#1493e6" />
              <span style={{ fontSize: '0.8rem', color: 'var(--text-secondary)' }}>{t}</span>
            </div>
          ))}
        </div>
      </div>

      <style>{`
        @media(max-width:640px){div[style*="grid-template-columns: 1fr 1fr"]{grid-template-columns:1fr!important}}
        .spin { animation: spin 0.8s linear infinite; }
        @keyframes spin { from { transform: rotate(0deg); } to { transform: rotate(360deg); } }
      `}</style>
    </div>
  );
};

export default GetQuotePage;